import React, { useEffect, useRef, useState } from "react";
import { CrudModal, AutoComplete } from "../../components/_index";
import AssigneePicker from "../../components/AssigneePicker";
import VoiceRecorder, {
  buildVoiceFile,
} from "../../components/CommonMail/VoiceRecorder";
import { Formik, Form, Field } from "formik";
import Grid from "@mui/material/Grid2";
import { Typography, Button, Box } from "@mui/material";
import * as yup from "yup";
import { useDispatch } from "react-redux";
import { showToast } from "../../redux/Reducer/toastReducer";
import { getCategory, getRole } from "../../services/category.service";
import { getSpecificUser } from "../../services/user.service";
import { createTicket } from "../../services/ticket.service";
import { FileUploader } from "react-drag-drop-files";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";

// Same completion window the ticket timers fall back to.
const DEFAULT_COMPLETE_IN_DAYS = 2;
const DEPARTMENT_ROLES = ["HR", "PROJECT MANAGER", "FEEDBACK-ADMIN", "IT SUPPORT"];
// The completion window is only chosen for IT Support tickets; every other
// department gets the backend default.
const IT_SUPPORT_ROLE = "IT SUPPORT";

const validationSchema = yup.object().shape({
  createdBy: yup.string().required("Select the staff this ticket is for *"),
  assignedDepartmentId: yup.string().required("Select the Department *"),
  assignedTo: yup
    .string()
    .required("Select the Assignee *")
    .test(
      "not-same-as-staff",
      "Assignee and the staff the ticket is for cannot be the same",
      function (value) {
        return !value || value !== this.parent.createdBy;
      }
    ),
  subject: yup.string().trim().required("Enter the title *"),
  message: yup.string().trim().required("Enter the description *"),
  expiry: yup.mixed().when("departmentRole", {
    is: IT_SUPPORT_ROLE,
    then: () =>
      yup
        .number()
        .typeError("Enter number of days")
        .integer("Enter whole days")
        .min(1, "Must be at least 1 day")
        .required("Enter number of days to complete *"),
  }),
});

const CreateTicket = ({ handleClose, onCreated }) => {
  const dispatch = useDispatch();
  const [department, setDepartment] = useState([]);
  // Assignable users grouped by their role short name (HR, PM, Admin, IT Support)
  const [userData, setUserData] = useState({});
  const [categoryData, setCategoryData] = useState([]);
  const [fileName, setFileName] = useState("");
  const [saving, setSaving] = useState(false);
  // Optional voice note, recorded with the same recorder the chat reply uses
  const [voice, setVoice] = useState(null);
  const [recording, setRecording] = useState(false);
  const voiceRef = useRef(null);
  voiceRef.current = voice;

  useEffect(
    () => () => {
      if (voiceRef.current?.url) URL.revokeObjectURL(voiceRef.current.url);
    },
    []
  );

  const formikRef = useRef(null);
  const defaultAssigneeApplied = useRef(false);

  const initialValues = {
    createdBy: "",
    assignedDepartmentId: "",
    department: "",
    departmentRole: "",
    assignedTo: "",
    categoryId: "",
    subject: "",
    message: "",
    expiry: DEFAULT_COMPLETE_IN_DAYS,
    file: null,
  };

  useEffect(() => {
    getRole()
      .then((res) => {
        const roles = (res?.data || [])
          .filter((e) => DEPARTMENT_ROLES.includes(e.name))
          .map((e) => ({ ...e, label: e.shortName, id: e._id }));
        setDepartment(roles);
      })
      .catch((error) => console.log(error));

    getSpecificUser()
      .then((res) => {
        const grouped = (res?.data || []).reduce((acc, el) => {
          const shortName = el?.roleData?.shortName;
          if (!acc[shortName]) acc[shortName] = [];
          acc[shortName].push({ ...el, label: `${el?.name}` });
          return acc;
        }, {});
        setUserData(grouped);
      })
      .catch((error) => console.log(error));
  }, []);

  // Once the departments and assignable users are in, pre-select the
  // logged-in user as the assignee (and their department, which the assignee
  // list depends on). Applied once and only into empty fields, so anything
  // the user already picked is left alone.
  useEffect(() => {
    if (defaultAssigneeApplied.current || !department.length) return;
    const groups = Object.entries(userData);
    if (!groups.length) return;
    defaultAssigneeApplied.current = true;

    const userId = localStorage.getItem("userId");
    const inGroup = (shortName) =>
      (userData[shortName] || []).some((u) => u._id === userId);
    // A user can be listed under more than one department; the role they
    // logged in with wins, else the first department they are listed under.
    const loginRole = department.find(
      (d) => d.name === localStorage.getItem("role")
    );
    const role =
      loginRole && inGroup(loginRole.label)
        ? loginRole
        : department.find((d) => inGroup(d.label));
    const form = formikRef.current;
    if (!role || !form || form.values.assignedDepartmentId) return;

    form.setFieldValue("assignedDepartmentId", role._id);
    form.setFieldValue("department", role.label);
    form.setFieldValue("departmentRole", role.name);
    form.setFieldValue("assignedTo", userId);
    loadCategories(role._id);
  }, [department, userData]);

  const loadCategories = (departmentRoleId) => {
    setCategoryData([]);
    if (!departmentRoleId) return;
    getCategory({ departmentRoleId })
      .then((res) => {
        setCategoryData(
          (res?.data || []).map((el) => ({ ...el, label: el?.name }))
        );
      })
      .catch((error) => console.log(error));
  };

  const onSubmit = async (values) => {
    if (recording) {
      dispatch(
        showToast({
          message: "Stop the voice recording before creating the ticket",
          type: "error",
        })
      );
      return;
    }
    try {
      setSaving(true);
      const payload = {
        createdBy: values.createdBy,
        assignedTo: values.assignedTo,
        assignedBy: localStorage.getItem("userId"),
        assignedDepartmentId: values.assignedDepartmentId,
        department:
          values.department === "Admin" ? "Management" : values.department,
        isManagement: values.department === "Admin",
        subject: values.subject.trim(),
        message: values.message.trim(),
        ...(values.departmentRole === IT_SUPPORT_ROLE && {
          expiry: Number(values.expiry),
        }),
        ...(values.categoryId && { categoryId: values.categoryId }),
        ...(voice && { voiceDuration: voice.duration }),
      };

      const formData = new FormData();
      formData.append("values", JSON.stringify(payload));
      if (values.file) {
        formData.append("file", values.file);
      }
      // After the file, so the attachment stays the ticket's first document
      if (voice) {
        formData.append("voice", buildVoiceFile(voice));
      }

      const res = await createTicket(formData);
      dispatch(
        showToast({
          message: `Ticket ${res?.data?.ticketId || ""} created and assigned successfully`,
          type: "success",
        })
      );
      onCreated();
    } catch (error) {
      dispatch(
        showToast({
          message: error?.data?.message || "Failed to create ticket",
          type: "error",
        })
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <CrudModal
      title="Create Ticket"
      open={true}
      handleClose={handleClose}
      maxWidth="md"
    >
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        {({ touched, errors, setFieldValue, values }) => (
          <div className="formContainer" style={{ height: "fit-content" }}>
            <Form>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 12 }}>
                  <Typography className="inputTitle" sx={{ fontSize: "12px" }}>
                    Ticket For (Staff)
                  </Typography>
                  <AssigneePicker
                    value={values.createdBy}
                    onChange={(id) => setFieldValue("createdBy", id || "")}
                    placeholder="Search and select the staff"
                  />
                  <div className="form-error">
                    {touched.createdBy && errors.createdBy
                      ? errors.createdBy
                      : null}
                  </div>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography className="inputTitle" sx={{ fontSize: "12px" }}>
                    Department
                  </Typography>
                  <AutoComplete
                    options={department}
                    value={values.assignedDepartmentId}
                    onChange={(e, v) => {
                      setFieldValue("assignedDepartmentId", v?._id || "");
                      setFieldValue("department", v?.label || "");
                      setFieldValue("departmentRole", v?.name || "");
                      // Assignee and category both depend on the department
                      setFieldValue("assignedTo", "");
                      setFieldValue("categoryId", "");
                      loadCategories(v?._id);
                    }}
                    placeholder="Select Department to assign"
                  />
                  <div className="form-error">
                    {touched.assignedDepartmentId &&
                    errors.assignedDepartmentId
                      ? errors.assignedDepartmentId
                      : null}
                  </div>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography className="inputTitle" sx={{ fontSize: "12px" }}>
                    Name of the Assignee
                  </Typography>
                  {/* Keyed on the department: AutoComplete keeps its typed text
                      when the value is cleared (clearOnBlur is off), so it is
                      remounted to drop the previous department's assignee. */}
                  <AutoComplete
                    key={values.assignedDepartmentId}
                    options={userData[values.department] || []}
                    value={values.assignedTo}
                    onChange={(e, v) => {
                      setFieldValue("assignedTo", v ? v._id : "");
                    }}
                    placeholder={
                      values.department
                        ? "Select user to assign"
                        : "Select Department first"
                    }
                    disabled={!values.department}
                  />
                  <div className="form-error">
                    {touched.assignedTo && errors.assignedTo
                      ? errors.assignedTo
                      : null}
                  </div>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography className="inputTitle" sx={{ fontSize: "12px" }}>
                    Category ( optional )
                  </Typography>
                  <AutoComplete
                    key={values.assignedDepartmentId}
                    options={categoryData}
                    value={values.categoryId}
                    onChange={(e, v) => setFieldValue("categoryId", v?._id || "")}
                    placeholder={
                      values.department
                        ? "Select Category"
                        : "Select Department first"
                    }
                    disabled={!values.department}
                  />
                </Grid>
                {values.departmentRole === IT_SUPPORT_ROLE ? (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography className="inputTitle" sx={{ fontSize: "12px" }}>
                      Complete Within (Days)
                    </Typography>
                    <Field
                      name="expiry"
                      type="number"
                      min={1}
                      step={1}
                      className="InputField"
                      placeholder="Enter number of days"
                    />
                    <div className="form-error">
                      {touched.expiry && errors.expiry ? errors.expiry : null}
                    </div>
                  </Grid>
                ) : null}
                <Grid size={{ xs: 12, sm: 12 }}>
                  <Typography className="inputTitle" sx={{ fontSize: "12px" }}>
                    Title
                  </Typography>
                  <Field
                    name="subject"
                    className="InputField"
                    placeholder="Enter Title"
                  />
                  <div className="form-error">
                    {touched.subject && errors.subject ? errors.subject : null}
                  </div>
                </Grid>
                <Grid size={{ xs: 12, sm: 12 }}>
                  <Typography className="inputTitle" sx={{ fontSize: "12px" }}>
                    Description
                  </Typography>
                  <Field
                    name="message"
                    as="textarea"
                    className="InputField"
                    style={{ height: "100px" }}
                    placeholder="Write the ticket description here"
                  />
                  <div className="form-error">
                    {touched.message && errors.message ? errors.message : null}
                  </div>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }} className="File-Upload">
                  <Typography className="inputTitle" sx={{ fontSize: "12px" }}>
                    File Attachment ( optional )
                  </Typography>
                  <FileUploader
                    name="file"
                    handleChange={(file) => {
                      if (file?.name) {
                        setFileName(file.name);
                        setFieldValue("file", file);
                      }
                    }}
                    multiple={false}
                  />
                  <Box className="uploadIcon">
                    <React.Fragment>
                      <CloudUploadOutlinedIcon className="cloud-icon" />
                      {fileName
                        ? fileName
                        : "Drag & Drop or click to upload files"}
                    </React.Fragment>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography className="inputTitle" sx={{ fontSize: "12px" }}>
                    Voice Message ( optional )
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      minHeight: "44px",
                    }}
                  >
                    <VoiceRecorder
                      voice={voice}
                      onChange={setVoice}
                      onRecordingChange={setRecording}
                      disabled={saving}
                      onError={(message) =>
                        dispatch(showToast({ message, type: "error" }))
                      }
                    />
                    {!voice && !recording ? (
                      <Typography sx={{ fontSize: "12px", color: "#757575" }}>
                        Click the mic to record ( max 5 min )
                      </Typography>
                    ) : null}
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }} mt={2}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: "20px",
                    }}
                  >
                    <Button className="cancelButton" onClick={handleClose}>
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="addButton"
                      disabled={saving}
                    >
                      {saving ? "Creating..." : "Create"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          </div>
        )}
      </Formik>
    </CrudModal>
  );
};

export default CreateTicket;
